import { z } from "zod"

import { DescriptionCleanupConfigSchema } from "../../analysis/cleanup-config-schema.js"
import { ApiRowQuestionSchema } from "./transaction-questions.js"

/**
 * The enrichment surface: workspace description-cleanup config, the
 * recompute job the client polls, and the Categorise rows table (PRD:
 * engagement-analysis-layer, phases 3 and 4).
 *
 * These are the single definition of the request and response shapes: the
 * API routes import them directly and the api-client parses responses
 * through them, so the two can never disagree.
 */

/**
 * The wire shape is the stored shape: one schema, defined next to the
 * transform it configures, aliased here so the surface reads Api-prefixed
 * like its neighbours.
 */
export const ApiCleanupConfigSchema = DescriptionCleanupConfigSchema

export type ApiCleanupConfig = z.infer<typeof ApiCleanupConfigSchema>

export const ApiCleanupConfigResponseSchema = z.object({
  config: ApiCleanupConfigSchema,
  /**
   * Up to ten real descriptions from the workspace, oldest documents first,
   * so the settings panel can preview the transform locally with the same
   * domain function the pipeline runs.
   */
  sampleDescriptions: z.array(z.string()),
})

export type ApiCleanupConfigResponse = z.infer<
  typeof ApiCleanupConfigResponseSchema
>

export const ApiSaveCleanupConfigBodySchema = z.object({
  config: ApiCleanupConfigSchema,
})

export type ApiSaveCleanupConfigBody = z.infer<
  typeof ApiSaveCleanupConfigBodySchema
>

export const ApiEnrichmentRecomputeStageSchema = z.enum([
  "clean",
  "categorise",
  "pair",
  "cluster",
])

export type ApiEnrichmentRecomputeStage = z.infer<
  typeof ApiEnrichmentRecomputeStageSchema
>

export const ApiEnrichmentRecomputeJobSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["queued", "running", "succeeded", "failed"]),
  fromStage: ApiEnrichmentRecomputeStageSchema,
  totalRows: z.number().int(),
  processedRows: z.number().int(),
  changedRows: z.number().int(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  lastErrorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type ApiEnrichmentRecomputeJob = z.infer<
  typeof ApiEnrichmentRecomputeJobSchema
>

export const ApiEnrichmentRecomputeStatusResponseSchema = z.object({
  job: ApiEnrichmentRecomputeJobSchema.nullable(),
})

export type ApiEnrichmentRecomputeStatusResponse = z.infer<
  typeof ApiEnrichmentRecomputeStatusResponseSchema
>

export const ApiSaveCleanupConfigResponseSchema = z.object({
  config: ApiCleanupConfigSchema,
  /** The recompute the save enqueued, for immediate progress polling. */
  job: ApiEnrichmentRecomputeJobSchema.nullable(),
})

export type ApiSaveCleanupConfigResponse = z.infer<
  typeof ApiSaveCleanupConfigResponseSchema
>

export const ApiTriggerEnrichmentRecomputeBodySchema = z.object({
  fromStage: ApiEnrichmentRecomputeStageSchema,
})

export type ApiTriggerEnrichmentRecomputeBody = z.infer<
  typeof ApiTriggerEnrichmentRecomputeBodySchema
>

/** The stable row identity enrichments key on; never extracted_records.id. */
export const ApiEnrichmentRowKeySchema = z.object({
  documentId: z.string().uuid(),
  recordType: z.string().min(1).max(80),
  recordIndex: z.number().int().min(0),
})

export type ApiEnrichmentRowKey = z.infer<typeof ApiEnrichmentRowKeySchema>

export const categoriseRowsPageSizeMax = 100
export const categoriseSelectionMax = 500

export const ApiCategorisedRowsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(categoriseRowsPageSizeMax)
    .default(50),
  uncategorised: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
  stale: z
    .enum(["true", "false"])
    .transform((value) => value === "true")
    .optional(),
  accountId: z.string().uuid().optional(),
  /** One document's rows, so the review screen can show each row's category
   * without pulling the whole workspace. */
  documentId: z.string().uuid().optional(),
  /**
   * One vendor group's rows, for the Review screen's expansion. The empty
   * string is a real value: it is the group of rows whose description
   * cleaned away to nothing, which still carry money and must stay
   * addressable.
   */
  counterpartyKey: z.string().max(500).optional(),
  search: z.string().trim().min(1).max(200).optional(),
})

export type ApiCategorisedRowsQuery = z.infer<
  typeof ApiCategorisedRowsQuerySchema
>

export const ApiCategorisedRowSchema = z.object({
  documentId: z.string().uuid(),
  recordType: z.string(),
  recordIndex: z.number().int(),
  recordDate: z.string().nullable(),
  description: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  cleanedDescription: z.string().nullable(),
  counterpartyKey: z.string().nullable(),
  accountId: z.string().uuid().nullable(),
  categoryId: z.string().uuid().nullable(),
  categorySource: z.enum(["rule", "manual", "none"]),
  categoryRuleId: z.string().uuid().nullable(),
  excluded: z.boolean(),
  stale: z.boolean(),
  documentFilename: z.string().nullable(),
  /** The open question the client has on this row, when it is live. */
  question: ApiRowQuestionSchema.nullable().optional(),
})

export type ApiCategorisedRow = z.infer<typeof ApiCategorisedRowSchema>

export const ApiCategorisedRowsResponseSchema = z.object({
  rows: z.array(ApiCategorisedRowSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(categoriseRowsPageSizeMax),
  totalRows: z.number().int(),
  uncategorisedCount: z.number().int(),
  /**
   * Sum of the absolute amounts of uncategorised rows, as a decimal string:
   * "how much money has no category yet", which is the number the banner
   * leads with.
   */
  uncategorisedTotal: z.string(),
  staleCount: z.number().int(),
})

export type ApiCategorisedRowsResponse = z.infer<
  typeof ApiCategorisedRowsResponseSchema
>

export const ApiSetRowCategoryBodySchema = z.object({
  rows: z.array(ApiEnrichmentRowKeySchema).min(1).max(categoriseSelectionMax),
  categoryId: z.string().uuid(),
})

export type ApiSetRowCategoryBody = z.infer<typeof ApiSetRowCategoryBodySchema>

export const ApiResetRowCategoriesBodySchema = z.object({
  rows: z.array(ApiEnrichmentRowKeySchema).min(1).max(categoriseSelectionMax),
})

export type ApiResetRowCategoriesBody = z.infer<
  typeof ApiResetRowCategoriesBodySchema
>

export const ApiConfirmStaleEnrichmentsBodySchema = z.object({
  /** Omitted means every stale row in the workspace: the confirm-all. */
  rows: z
    .array(ApiEnrichmentRowKeySchema)
    .min(1)
    .max(categoriseSelectionMax)
    .optional(),
})

export type ApiConfirmStaleEnrichmentsBody = z.infer<
  typeof ApiConfirmStaleEnrichmentsBodySchema
>

export const ApiEnrichmentMutationResponseSchema = z.object({
  updatedRows: z.number().int(),
})

export type ApiEnrichmentMutationResponse = z.infer<
  typeof ApiEnrichmentMutationResponseSchema
>

/**
 * The Review screen's read model (PRD: review-payables-redesign-build).
 *
 * One row per vendor rather than per transaction, because a category is a
 * fact about a vendor and asking about it once per transaction is the whole
 * problem this screen exists to remove. Grouping is on
 * `record_enrichments.counterparty_key`, the normalised vendor identity
 * phase 4 rules and phase 7 rollups already group on; there is deliberately
 * no second vendor concept.
 */
export const ApiReviewVendorSchema = z.object({
  counterpartyKey: z.string(),
  /** Display label derived from the key; the key itself is shown as the sub-line. */
  label: z.string(),
  rowCount: z.number().int(),
  /** Signed decimal string, consistent with ApiCategorisedRow.amount. */
  totalAmount: z.string(),
  currency: z.string().nullable(),
  accountIds: z.array(z.string().uuid()),
  firstDate: z.string().nullable(),
  lastDate: z.string().nullable(),
  suggestedCategoryId: z.string().uuid().nullable(),
  /**
   * How the suggestion was reached, first hit wins: a rule already matches
   * the key, or this workspace categorised the key before. `none` is a legitimate outcome, not a failure: a Zelle payment to
   * a person has no answer in the data, and guessing is worse than saying so.
   */
  suggestionSource: z.enum(["rule", "prior_decision", "none"]),
  staleCount: z.number().int(),
})

export type ApiReviewVendor = z.infer<typeof ApiReviewVendorSchema>

export const ApiReviewVendorsResponseSchema = z.object({
  vendors: z.array(ApiReviewVendorSchema),
  totalVendors: z.number().int(),
  uncategorisedRowCount: z.number().int(),
  uncategorisedTotal: z.string(),
  suggestedVendorCount: z.number().int(),
  staleCount: z.number().int(),
})

export type ApiReviewVendorsResponse = z.infer<
  typeof ApiReviewVendorsResponseSchema
>

export const reviewVendorsPageSizeMax = 200

export const ApiReviewVendorsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(reviewVendorsPageSizeMax)
    .default(10),
  /**
   * The filter chips. `all` is every uncategorised vendor; `suggested` and
   * `no_suggestion` split it on whether an Accept button exists; `stale` is
   * "changed in a re-read".
   */
  filter: z
    .enum(["all", "suggested", "no_suggestion", "stale"])
    .default("all"),
  search: z.string().trim().min(1).max(200).optional(),
})

export type ApiReviewVendorsQuery = z.infer<typeof ApiReviewVendorsQuerySchema>

export const acceptVendorsMax = 200

/**
 * Accepting works by vendor key rather than by row key, which sidesteps the
 * `categoriseSelectionMax` cap entirely: "Accept 24 suggestions" spans
 * roughly 980 rows, and this is one round trip rather than several.
 */
export const ApiAcceptVendorCategoriesBodySchema = z.object({
  vendors: z
    .array(
      z.object({
        counterpartyKey: z.string().min(1),
        categoryId: z.string().uuid(),
        /** Omit for the whole group; present when the user peeled rows off. */
        rows: z.array(ApiEnrichmentRowKeySchema).optional(),
      })
    )
    .min(1)
    .max(acceptVendorsMax),
  /** False for a one-time call the user does not want repeated. */
  createRules: z.boolean().default(true),
})

export type ApiAcceptVendorCategoriesBody = z.infer<
  typeof ApiAcceptVendorCategoriesBodySchema
>

export const ApiAcceptVendorCategoriesResponseSchema = z.object({
  updatedRows: z.number().int(),
  rulesCreated: z.number().int(),
})

export type ApiAcceptVendorCategoriesResponse = z.infer<
  typeof ApiAcceptVendorCategoriesResponseSchema
>
